import { useEffect } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getHostNotificationsApi,
  getUnreadCountApi,
  markNotificationReadApi,
  markAllNotificationsReadApi,
} from '../api/notifications';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const is401or403 = (e) => {
  const msg = (e?.message ?? '').toLowerCase();
  return msg.includes('401') || msg.includes('403') ||
         msg.includes('unauthorized') || msg.includes('forbidden');
};

export const NOTIFICATIONS_KEY = ['notifications'];
export const UNREAD_COUNT_KEY  = ['notifications', 'unread-count'];

/* ── Unread bell badge (with focus refresh + 60s poll fallback) ───────────── */
export const useUnreadCount = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: UNREAD_COUNT_KEY,
    queryFn:  getUnreadCountApi,
    enabled:  !!user,
    refetchOnWindowFocus: true,
    refetchInterval: 60000,
    staleTime: 30000,
  });

  // Refresh on app foreground (visibility change)
  useEffect(() => {
    if (!user) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user, queryClient]);

  return {
    unread: query.data?.unread ?? 0,
    ...query,
  };
};

/* ── Paginated infinite list (filtered by is_read) ────────────────────────── */
export const useNotificationsList = ({ filter = 'all', limit = 20 } = {}) => {
  const { user, logout } = useAuth();

  const query = useInfiniteQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'list', filter, limit],
    queryFn:  ({ pageParam }) => {
      const params = { page: pageParam, limit };
      if (filter === 'unread') params.is_read = false;
      if (filter === 'read')   params.is_read = true;
      return getHostNotificationsApi(params);
    },
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page < last.totalPages ? last.page + 1 : undefined,
    enabled: !!user,
    staleTime: 30000,
  });

  useEffect(() => {
    if (query.error && is401or403(query.error)) logout();
  }, [query.error, logout]);

  return query;
};

/* ── Mutations ────────────────────────────────────────────────────────────── */
export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();
  const { logout }  = useAuth();
  const { error: showError } = useToast();

  return useMutation({
    mutationFn: markNotificationReadApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    },
    onError: (e) => {
      if (is401or403(e)) { logout(); return; }
      // 404 here means "already read or not yours" — silently ignore.
      if (!String(e?.message ?? '').includes('404')) {
        showError(e?.message || 'Failed to mark as read.');
      }
    },
  });
};

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();
  const { logout }  = useAuth();
  const { success, error: showError } = useToast();

  return useMutation({
    mutationFn: markAllNotificationsReadApi,
    onSuccess: () => {
      success('All notifications marked as read.');
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    },
    onError: (e) => {
      if (is401or403(e)) { logout(); return; }
      showError(e?.message || 'Failed to mark all as read.');
    },
  });
};
